import { useMemo } from "react";
import { Button, Flex, ModalV2, QueryLoader, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { TaxDataSourceAssignmentStatus } from "~/graphql/supergraphEnums";
import {
  useGetPositionsForCaseQuery,
  useGetTaxDataSourceAssignmentsQuery,
} from "~/graphql/supergraphHooks";
import { type BaseDocumentType } from "~/graphql/supergraphTypes";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { POSITION_CASE_TYPES } from "../../DocumentManagement/DocumentDetailsRightSidebar/DocumentStatusCard/FatcaAssignmentModal/constants";
import { getSourceTypeLabel } from "../../utils/taxDocuments";
import * as styles from "./styles_vanilla.css";

export type DeleteSourceDocumentModalProps = {
  documentId: number;
  documentName: string;
  documentType: BaseDocumentType;
  profileId: number;
  onClose: () => void;
  onDelete: () => void;
  isDeleting?: boolean;
};

type PositionItem = {
  name: string;
  caseId: number;
};

export const DeleteSourceDocumentModal = ({
  documentId,
  documentName,
  documentType,
  profileId,
  onClose,
  onDelete,
  isDeleting = false,
}: DeleteSourceDocumentModalProps) => {
  const sourceType = getSourceTypeLabel(documentType);
  const isFatca = documentType === "w_series_tax_form";

  // CRS needs positions data, FATCA just needs profile name
  const assignmentsResult = useGetTaxDataSourceAssignmentsQuery({
    variables: { input: { documentId } },
    skip: isFatca,
  });

  const positionsResult = useGetPositionsForCaseQuery({
    variables: { profileId, caseTypes: [...POSITION_CASE_TYPES] },
    fetchPolicy: "network-only",
  });

  const positionsData = positionsResult.data;

  const profileName = useMemo(() => {
    const allCases = positionsData?.complianceProfile?.complianceCases ?? [];
    for (const c of allCases) {
      if (c.complianceCase?.investingProfile?.id === profileId) {
        return getInvestingProfileName(c.complianceCase.investingProfile) ?? "";
      }
    }
    return "";
  }, [positionsData, profileId]);

  // Only compute positions for CRS documents
  const positions: PositionItem[] = useMemo(() => {
    if (isFatca) return [];
    const assignmentsData = assignmentsResult.data;
    const referencedCaseIds = new Set(
      (assignmentsData?.taxDataSourceAssignments ?? [])
        .filter((a) => a.status === TaxDataSourceAssignmentStatus.Assigned)
        .map((a) => a.referencedCaseId)
        .filter((id): id is number => typeof id === "number" && id > 0),
    );
    const allPositions = positionsData?.complianceProfile?.positions ?? [];

    const result: PositionItem[] = [];
    for (const p of allPositions) {
      const oc = p.onboardingCase;
      if (!oc || !referencedCaseIds.has(oc.id)) continue;

      const investingName = getInvestingProfileName(oc.investingProfile) || "";
      const entityName = oc.externalEntity?.name || "";
      result.push({
        name: investingName && entityName ? `${investingName} in ${entityName}` : investingName,
        caseId: oc.id,
      });
    }
    return result;
  }, [isFatca, assignmentsResult.data, positionsData]);

  const queryResults = isFatca ? [positionsResult] : [assignmentsResult, positionsResult];

  return (
    <ModalV2
      data-testid="delete-source-document-modal"
      show={true}
      onHide={onClose}
      title={`Delete ${sourceType} source document`}
      size="medium"
      closeOnClickOutside={false}
    >
      <ModalV2.Body>
        <QueryLoader result={queryResults}>
          {isFatca ? (
            // FATCA: Simple modal with just profile name
            <Text className={styles.description}>
              Are you sure you want to delete this document? Deleting this document will remove
              the assignment of {sourceType} source document for {profileName || documentName}.
            </Text>
          ) : positions.length > 0 ? (
            // CRS with positions
            <>
              <Text className={styles.description}>
                Are you sure you want to delete this document? Deleting this document will
                remove the assignment of {sourceType} source document from the following
                positions for {profileName || documentName}:
              </Text>
              <div className={styles.positionsList}>
                <Text className={styles.positionsHeader}>Positions</Text>
                {positions.map((position) => (
                  <Text key={position.caseId} className={styles.positionItem}>
                    {position.name}
                  </Text>
                ))}
              </div>
              <Text variant="regular-md" css={{ marginTop: Sizes.MD }}>
                This action cannot be undone and there will be no {sourceType} source document
                assigned.
              </Text>
            </>
          ) : (
            // CRS without positions (fallback)
            <Text className={styles.description}>
              Are you sure you want to delete this document? Deleting this document will remove
              the assignment of {sourceType} source document for {profileName || documentName}.
            </Text>
          )}
        </QueryLoader>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex gap={Sizes.MD} justifyContent="flex-end">
          <Button onClick={onClose} variant="neutral" disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            data-testid="delete-document-button"
            variant="danger"
            onClick={onDelete}
            disabled={isDeleting}
            isLoading={isDeleting}
          >
            Delete document
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
